import React, { useEffect, useState } from 'react';
import { getAllBuckets, clearAllBuckets } from '../utils/abtest';
import { getActiveExperiments, getVariantDisplayName } from '../utils/experiments';
import { FaFlask, FaTimes, FaSync } from 'react-icons/fa';

/**
 * Experiment Debug Component
 * 
 * Shows current A/B test assignments and provides controls for testing.
 * Only visible when ?debug=true is in URL.
 */
export default function ExperimentDebug() {
  const [isVisible, setIsVisible] = useState(false);
  const [isMinimized, setIsMinimized] = useState(false);
  const [buckets, setBuckets] = useState({});
  const [experiments, setExperiments] = useState({});

  useEffect(() => {
    // Only show debug panel when explicitly requested via ?debug=true
    const params = new URLSearchParams(window.location.search);
    const showDebug = params.get('debug') === 'true';
    
    setIsVisible(showDebug);

    if (showDebug) {
      // Load current bucket assignments
      setBuckets(getAllBuckets());
      
      // Load experiments from Edge Config (async)
      getActiveExperiments().then(exps => {
        setExperiments(exps || {});
      });
    }
  }, []);

  const handleRefresh = () => {
    setBuckets(getAllBuckets());
    // Also refresh experiments from Edge Config
    getActiveExperiments().then(exps => {
      setExperiments(exps || {});
    });
  };

  const handleClearAll = () => {
    if (window.confirm('Clear all experiment assignments? Page will reload.')) {
      clearAllBuckets();
      window.location.reload();
    }
  };

  const handleForceVariant = (experimentName, variant) => {
    const currentUrl = new URL(window.location.href);
    currentUrl.searchParams.set(`ab-${experimentName}`, variant);
    // Preserve debug parameter so panel stays visible
    currentUrl.searchParams.set('debug', 'true');
    window.location.href = currentUrl.toString();
  };

  if (!isVisible) return null;

  const experimentKeys = Object.keys(experiments);

  return (
    <div className="fixed bottom-4 right-4 z-[9999] font-mono text-xs">
      <div 
        className={`bg-gray-900 text-white rounded-lg shadow-2xl border border-gray-700 transition-all duration-300 ${
          isMinimized ? 'w-12 h-12' : 'w-96'
        }`}
      >
        {/* Header */}
        <div 
          className="flex items-center justify-between p-3 border-b border-gray-700 cursor-pointer hover:bg-gray-800"
          onClick={() => setIsMinimized(!isMinimized)}
        >
          <div className="flex items-center gap-2">
            <FaFlask className="text-green-400" />
            {!isMinimized && <span className="font-bold">A/B Test Debug</span>}
          </div>
          {!isMinimized && (
            <button
              onClick={(e) => {
                e.stopPropagation();
                setIsVisible(false);
              }}
              className="hover:text-red-400 transition-colors"
              title="Hide debug panel (remove ?debug=true from URL to hide permanently)"
            >
              <FaTimes />
            </button>
          )}
        </div>

        {/* Content */}
        {!isMinimized && (
          <div className="p-3 max-h-[500px] overflow-y-auto">
            {/* Active Experiments */}
            <div className="mb-4">
              <div className="flex items-center justify-between mb-2">
                <div className="text-gray-400 text-[10px] uppercase tracking-wide">
                  Active Experiments ({experimentKeys.length})
                </div>
                <button
                  onClick={handleRefresh}
                  className="text-blue-400 hover:text-blue-300 transition-colors"
                  title="Refresh"
                >
                  <FaSync className="text-xs" />
                </button>
              </div>

              {experimentKeys.map(key => {
                const experiment = experiments[key];
                const currentBucket = buckets[key];
                
                return (
                  <div 
                    key={key} 
                    className="bg-gray-800 rounded p-3 mb-3 border border-gray-700"
                  >
                    <div className="font-bold text-white mb-1">
                      {experiment.displayName}
                    </div>
                    
                    {currentBucket && (
                      <div className="mb-2">
                        <span className="text-gray-400">Current: </span>
                        <span className="text-green-400 font-bold">
                          {currentBucket} ({getVariantDisplayName(key, currentBucket)})
                        </span>
                      </div>
                    )}

                    {/* Variant Controls */}
                    <div className="flex gap-2 mt-2">
                      <button
                        onClick={() => handleForceVariant(key, 'A')}
                        className={`flex-1 px-3 py-2 rounded text-xs font-semibold transition-colors ${
                          currentBucket === 'A'
                            ? 'bg-green-500 text-white'
                            : 'bg-gray-700 text-gray-300 hover:bg-gray-600'
                        }`}
                      >
                        Force A
                      </button>
                      <button
                        onClick={() => handleForceVariant(key, 'B')}
                        className={`flex-1 px-3 py-2 rounded text-xs font-semibold transition-colors ${
                          currentBucket === 'B'
                            ? 'bg-green-500 text-white'
                            : 'bg-gray-700 text-gray-300 hover:bg-gray-600'
                        }`}
                      >
                        Force B
                      </button>
                    </div>

                    {/* Variant Details */}
                    <div className="mt-2 text-[10px] text-gray-500">
                      <div>A: {experiment.variants.A.description}</div>
                      <div>B: {experiment.variants.B.description}</div>
                    </div>
                  </div>
                );
              })}
            </div>

            {/* Actions */}
            <div className="border-t border-gray-700 pt-3">
              <button
                onClick={handleClearAll}
                className="w-full px-3 py-2 bg-red-600 hover:bg-red-700 text-white rounded font-semibold transition-colors"
              >
                Clear All Assignments
              </button>
            </div>

            {/* URL Parameters Help */}
            <div className="mt-3 pt-3 border-t border-gray-700 text-[10px] text-gray-500">
              <div className="mb-1 font-bold text-gray-400">URL Parameters:</div>
              {experimentKeys.map(key => (
                <div key={key}>?ab-{key}=A or B</div>
              ))}
              <div className="mt-2">?debug=true (show this panel)</div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

/**
 * Lightweight indicator when experiments are running
 * Shows a small badge in development
 */
export function ExperimentIndicator() {
  const [hasExperiments, setHasExperiments] = useState(false);

  useEffect(() => {
    const buckets = getAllBuckets();
    setHasExperiments(Object.keys(buckets).length > 0);
  }, []);

  if (!hasExperiments || process.env.NODE_ENV !== 'development') return null;

  return (
    <div className="fixed bottom-4 left-4 z-50">
      <div className="bg-purple-600 text-white px-3 py-1 rounded-full text-xs font-mono flex items-center gap-2 shadow-lg">
        <FaFlask className="text-xs" />
        <span>A/B Testing Active</span>
      </div>
    </div>
  );
}
